/**
 * telemetry-stats.ts — the Snippet Telemetry summary figures (O3, 11 October 2026).
 *
 * THE DEFECT: on 9 October 2026 08:25 ACDT the Australian zone's "Last 1h" showed 31 events and
 * "90% bot". 25 of the 31 were the platform's OWN synthetic probes (every one flagged bot by
 * design); the real traffic was 6 events, 3 of them bots — a 50% rate, not 90%. An operator who
 * reads that card as a threat signal was being shown the platform's own monitoring.
 *
 * THE RULE (Claude's implementation choice, in the brief for Jeff to overrule at UAT): the bot
 * percentage and its colour thresholds are computed over REAL traffic only — live and beta —
 * and synthetic is shown as its own count, never inside the percentage.
 */
export interface TelemetryStatRow {
  isBot: number;
  source: string;
  hostname?: string;
  domainToken: string;
}

export interface TelemetryStats {
  /** Every event in the window, synthetic included. */
  total: number;
  /** The platform's own probes and test beacons — shown as a count, never in the percentage. */
  synthetic: number;
  /** Real traffic: live + beta. */
  real: number;
  /** Bot-flagged events among the REAL traffic. */
  realBots: number;
  /** realBots / real, rounded — 0 when there is no real traffic. */
  botPct: number;
  live: number;
  beta: number;
  domains: number;
}

export function telemetryStats(events: ReadonlyArray<TelemetryStatRow>): TelemetryStats {
  let synthetic = 0, real = 0, realBots = 0, live = 0, beta = 0;
  const domains = new Set<string>();
  for (const e of events) {
    domains.add(e.hostname || e.domainToken);
    if (e.source === 'synthetic') { synthetic += 1; continue; }
    real += 1;
    if (e.source === 'live') live += 1;
    else if (e.source === 'beta') beta += 1;
    if (e.isBot === 1) realBots += 1;
  }
  return {
    total: events.length, synthetic, real, realBots,
    botPct: real > 0 ? Math.round((realBots / real) * 100) : 0,
    live, beta, domains: domains.size,
  };
}

/** The card's colour band, over the REAL-traffic percentage. */
export function botBand(botPct: number, real: number): 'ok' | 'warn' | 'crit' {
  if (real === 0) return 'ok';
  return botPct >= 50 ? 'crit' : botPct >= 20 ? 'warn' : 'ok';
}
